import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import { normalizeProjectModules } from "../../utils/projectModules";
import { isSuperAdmin } from "../../utils/roles";
import { getProjectBillingAccess } from "../../utils/projectBillingAccess";
import BillingAccessBanner from "./BillingAccessBanner";
import MobileDashboardShell from "./mobile/MobileDashboardShell";
import { isTrialProject } from "../../utils/trialOnboarding";
import logo from "../../images/LogoNavbar1.svg";

const drawerWidth = 280;
const BRAND = {
  orange: "#F35A3A",
  dark: "#19242F",
  bg: "#F6F7FB",
};

function navItemSx(isActive) {
  return {
    borderRadius: 2,
    mb: 0.75,
    color: isActive ? "#fff" : "rgba(255,255,255,0.78)",
    bgcolor: isActive ? BRAND.orange : "transparent",
    "&:hover": {
      bgcolor: isActive ? BRAND.orange : "rgba(255,255,255,0.08)",
      color: "#fff",
    },
    "& .MuiListItemIcon-root": {
      color: "inherit",
      minWidth: 40,
    },
  };
}

function SidebarContent({ onClose }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { projects, activeProject, setActiveProject } = useProjects();
  const navigate = useNavigate();
  const superAdmin = isSuperAdmin(user);
  const trialMode = !superAdmin && isTrialProject(activeProject);
  const modules = normalizeProjectModules(activeProject?.modules);
  const billingAccess = getProjectBillingAccess(activeProject);
  const operationsRestricted = !superAdmin && (billingAccess.isLandingOnly || billingAccess.isSuspended);
  const projectBase = activeProject?.slug
    ? `/dashboard/proyectos/${activeProject.slug}`
    : "/dashboard/proyectos";

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const go = (path) => {
    onClose?.();
    navigate(path);
  };

  const isPath = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const projectItems = [
    {
      key: "landing",
      visible: Boolean(activeProject) && modules.landing,
      label: "Diseñá tu Web",
      icon: <PublicRoundedIcon />,
      path: activeProject?.slug ? `${projectBase}/pagina` : "/dashboard/proyectos",
      active: location.pathname.includes("/pagina"),
    },
    {
      key: "products",
      visible: Boolean(activeProject) && modules.products && !modules.system,
      label: "Productos y servicios",
      icon: <Inventory2RoundedIcon />,
      path: `${projectBase}/productos`,
      active: location.pathname.includes("/productos"),
      restricted: operationsRestricted,
    },
    {
      key: "agenda",
      visible: Boolean(activeProject) && modules.products,
      label: "Agenda",
      icon: <EventAvailableRoundedIcon />,
      path: `${projectBase}/agenda`,
      active: location.pathname.includes("/agenda"),
      restricted: operationsRestricted,
    },
    {
      key: "orders",
      visible: Boolean(activeProject) && modules.orders,
      label: "Pedidos",
      icon: <ListAltRoundedIcon />,
      path: `${projectBase}/pedidos`,
      active: location.pathname.includes("/pedidos"),
      restricted: operationsRestricted,
    },
    {
      key: "system",
      visible: Boolean(activeProject) && (superAdmin || modules.system),
      label: "Gestión del negocio",
      icon: <WarehouseRoundedIcon />,
      path: `${projectBase}/sistema`,
      active: location.pathname.includes("/sistema"),
      restricted: operationsRestricted,
    },
    {
      key: "stats",
      visible: Boolean(activeProject) && modules.stats,
      label: "Estadísticas web",
      icon: <AnalyticsRoundedIcon />,
      path: `${projectBase}/estadisticas`,
      active: location.pathname.includes("/estadisticas"),
      restricted: operationsRestricted,
    },
  ];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: BRAND.dark, color: "#fff" }}>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box component="img" src={logo} alt="WebProfit" sx={{ width: 44, height: 44, objectFit: "contain" }} />
          <Box>
            <Typography sx={{ fontWeight: 950, letterSpacing: 0.4, lineHeight: 1 }}>
              WebProfit
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontWeight: 700 }}>
              {superAdmin ? "Superpanel" : "Panel cliente"}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ px: 2 }}>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)", fontWeight: 900, letterSpacing: 1 }}>
          PROYECTO ACTIVO
        </Typography>
        <Select
          fullWidth
          size="small"
          value={activeProject?.slug || ""}
          onChange={(event) => setActiveProject(event.target.value)}
          displayEmpty
          sx={{
            mt: 1,
            color: "#fff",
            bgcolor: "rgba(255,255,255,0.08)",
            borderRadius: 2,
            ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.18)" },
            ".MuiSvgIcon-root": { color: "#fff" },
          }}
        >
          <MenuItem value="" disabled>
            Sin proyectos
          </MenuItem>
          {projects.map((project) => (
            <MenuItem key={project.id || project.slug} value={project.slug}>
              {project.name}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 2 }} />

      <List sx={{ px: 2, flex: 1, overflowY: "auto" }}>
        <ListItemButton sx={navItemSx(isPath("/dashboard", true))} onClick={() => go("/dashboard")}>
          <ListItemIcon><DashboardRoundedIcon /></ListItemIcon>
          <ListItemText primary="Inicio" primaryTypographyProps={{ fontWeight: 850 }} />
        </ListItemButton>

        {superAdmin && (
          <>
            <ListItemButton sx={navItemSx(isPath("/dashboard/superpanel"))} onClick={() => go("/dashboard/superpanel")}>
              <ListItemIcon><AdminPanelSettingsRoundedIcon /></ListItemIcon>
              <ListItemText primary="Superpanel" primaryTypographyProps={{ fontWeight: 850 }} />
            </ListItemButton>
            <ListItemButton sx={navItemSx(isPath("/dashboard/usuarios"))} onClick={() => go("/dashboard/usuarios")}>
              <ListItemIcon><PeopleAltRoundedIcon /></ListItemIcon>
              <ListItemText primary="Usuarios" primaryTypographyProps={{ fontWeight: 850 }} />
            </ListItemButton>
            <ListItemButton sx={navItemSx(isPath("/dashboard/billing"))} onClick={() => go("/dashboard/billing")}>
              <ListItemIcon><WorkspacePremiumRoundedIcon /></ListItemIcon>
              <ListItemText primary="Precios y suscripciones" primaryTypographyProps={{ fontWeight: 850 }} />
            </ListItemButton>
          </>
        )}

        <ListItemButton sx={navItemSx(isPath("/dashboard/proyectos", true))} onClick={() => go("/dashboard/proyectos")}>
          <ListItemIcon><StorefrontRoundedIcon /></ListItemIcon>
          <ListItemText primary={superAdmin ? "Clientes / proyectos" : "Mis proyectos"} primaryTypographyProps={{ fontWeight: 850 }} />
        </ListItemButton>

        {activeProject && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2.25, mb: 1, px: 1 }}>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.42)", fontWeight: 900, letterSpacing: 1 }}>
              OPERACIÓN DEL PROYECTO
            </Typography>
            {trialMode ? (
              <Chip
                size="small"
                label="PRUEBA 30 DÍAS"
                sx={{
                  height: 22,
                  color: "#fff",
                  bgcolor: "rgba(243,90,58,.22)",
                  border: "1px solid rgba(243,90,58,.45)",
                  fontSize: 9,
                  fontWeight: 950,
                  letterSpacing: .6,
                }}
              />
            ) : null}
          </Stack>
        )}

        {projectItems.filter((item) => item.visible).map((item) => (
          <ListItemButton
            key={item.key}
            sx={navItemSx(item.active)}
            onClick={() => go(item.path)}
          >
            <ListItemIcon>{item.restricted ? <LockClockRoundedIcon /> : item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              secondary={item.restricted ? "Pausado por pago" : null}
              primaryTypographyProps={{ fontWeight: 850 }}
              secondaryTypographyProps={{
                sx: {
                  color: "rgba(255,255,255,.46)",
                  fontSize: ".68rem",
                  fontWeight: 850,
                },
              }}
            />
          </ListItemButton>
        ))}

        {activeProject && billingAccess.needsPaymentAction ? (
          <ListItemButton
            sx={{
              ...navItemSx(location.pathname.includes("/configuracion/suscripcion")),
              mt: 1,
              border: "1px solid rgba(243,90,58,.55)",
              bgcolor: location.pathname.includes("/configuracion/suscripcion")
                ? BRAND.orange
                : "rgba(243,90,58,.12)",
            }}
            onClick={() => go(`${projectBase}/configuracion/suscripcion`)}
          >
            <ListItemIcon><WorkspacePremiumRoundedIcon /></ListItemIcon>
            <ListItemText
              primary={billingAccess.isGrace ? "Resolver pago" : "Reactivar funciones"}
              secondary={billingAccess.isGrace ? "Antes del vencimiento" : "Landing sigue visible"}
              primaryTypographyProps={{ fontWeight: 950 }}
              secondaryTypographyProps={{ sx: { color: "rgba(255,255,255,.52)", fontSize: ".68rem" } }}
            />
          </ListItemButton>
        ) : null}

        <ListItemButton
          sx={navItemSx(
            activeProject?.slug
              ? location.pathname.includes(`${projectBase}/configuracion`)
              : false
          )}
          onClick={() =>
            go(
              activeProject?.slug
                ? `${projectBase}/configuracion/general`
                : "/dashboard/proyectos"
            )
          }
        >
          <ListItemIcon><SettingsRoundedIcon /></ListItemIcon>
          <ListItemText primary="Configuración" primaryTypographyProps={{ fontWeight: 850 }} />
        </ListItemButton>

        {modules.landing && (
          <ListItemButton sx={navItemSx(false)} onClick={() => go("/")}>
            <ListItemIcon><PublicRoundedIcon /></ListItemIcon>
            <ListItemText primary="Ver landing" primaryTypographyProps={{ fontWeight: 850 }} />
          </ListItemButton>
        )}
      </List>

      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
          <Avatar sx={{ bgcolor: BRAND.orange, fontWeight: 900 }}>{user?.name?.[0]?.toUpperCase() || "W"}</Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 900 }}>{user?.name || "Usuario"}</Typography>
            <Typography noWrap variant="caption" sx={{ color: "rgba(255,255,255,0.62)" }}>{user?.email}</Typography>
            <Typography noWrap variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.42)", fontWeight: 800 }}>
              {user?.globalRole || "client"}
            </Typography>
          </Box>
        </Stack>
        <Stack spacing={1}>
          <Button
            fullWidth
            variant={isPath("/dashboard/seguridad", true) ? "contained" : "outlined"}
            startIcon={<LockResetRoundedIcon />}
            onClick={() => go("/dashboard/seguridad")}
            sx={{
              color: "#fff",
              bgcolor: isPath("/dashboard/seguridad", true) ? BRAND.orange : "transparent",
              borderColor: isPath("/dashboard/seguridad", true) ? BRAND.orange : "rgba(255,255,255,0.22)",
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 850,
              "&:hover": {
                bgcolor: isPath("/dashboard/seguridad", true) ? BRAND.orange : "rgba(255,255,255,.08)",
                borderColor: isPath("/dashboard/seguridad", true) ? BRAND.orange : "rgba(255,255,255,.35)",
              },
            }}
          >
            Cambiar contraseña
          </Button>
          <Button fullWidth variant="outlined" startIcon={<LogoutRoundedIcon />} onClick={handleLogout} sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.22)", borderRadius: 2, textTransform: "none", fontWeight: 850 }}>
            Salir
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

export default function DashboardShell() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const { activeProject } = useProjects();

  if (!isMdUp) return <MobileDashboardShell />;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: BRAND.bg, display: "flex" }}>
      <Drawer variant="permanent" sx={{ width: drawerWidth, flexShrink: 0, [`& .MuiDrawer-paper`]: { width: drawerWidth, border: 0 } }}>
        <SidebarContent />
      </Drawer>

      <Box component="main" sx={{ flex: 1, minWidth: 0, py: 4 }}>
        <Container maxWidth="xl">
          <BillingAccessBanner project={activeProject} />
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
}
